import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Minus, Trash2, CreditCard, Smartphone, Tag, Receipt, Wine, Search, User, Settings, ShoppingCart, X, Check, Printer, Clock, Save, Bell, DollarSign, TrendingUp, RotateCcw, Wallet, ChevronDown, ChevronUp, UserCheck } from 'lucide-react';
import config from '../config/config';
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import CartContent from '../components/PosCart';
import BottomBar from '../components/BottomBar'; 
// Quicksand font import
const fontLink = document.createElement('link');
fontLink.rel = 'stylesheet';
fontLink.href = 'https://fonts.googleapis.com/css2?family=Quicksand:wght@400;500;600;700&display=swap';
document.head.appendChild(fontLink);

const alertSound = new Audio('/alert.mp3');

// ─── Customer Selector Component ─────────────────────────────────────────────
const CustomerSelector = ({ customers, customerInfo, setCustomerInfo }) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const dropRef = useRef(null);

  const customerList = Array.isArray(customers) ? customers : [];

  const filtered = query.length > 0
    ? customerList.filter(c =>
        c.name?.toLowerCase().includes(query.toLowerCase()) ||
        c.contact?.toLowerCase().includes(query.toLowerCase()) ||
        c.email?.toLowerCase().includes(query.toLowerCase())
      )
    : customerList.slice(0, 8);

  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    setCustomerInfo(prev => ({
      ...prev,
      name: c.name || '',
      phone: c.contact || c.phone || '',
      email: c.email || '',
    }));
    setQuery(c.name || '');
    setIsOpen(false);
    setIsManual(false);
  };

  const clearSelection = () => {
    setSelectedCustomer(null);
    setQuery('');
    setCustomerInfo(prev => ({ ...prev, name: '', phone: '', email: '' }));
    setIsManual(false);
  };

  return (
    <div style={{ fontFamily: 'Quicksand, sans-serif' }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>
        Customer
      </label>

      {!isManual ? (
        <div ref={dropRef} style={{ position: 'relative' }}>
          <div style={{ position: 'relative' }}>
            <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', width: 15, height: 15, color: '#94a3b8' }} />
            <input
              value={query}
              onChange={e => { setQuery(e.target.value); setIsOpen(true); setSelectedCustomer(null); }}
              onFocus={() => setIsOpen(true)}
              placeholder="Search customer by name or phone…"
              style={{
                width: '100%', padding: '10px 36px 10px 34px', border: '1.5px solid #e2e8f0',
                borderRadius: 10, fontSize: 13, fontFamily: 'Quicksand, sans-serif',
                fontWeight: 500, outline: 'none', color: '#1e293b', background: '#f8fafc',
                boxSizing: 'border-box', transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => e.target.style.borderColor = '#93c5fd'}
              onMouseLeave={e => e.target.style.borderColor = selectedCustomer ? '#3b82f6' : '#e2e8f0'}
            />
            {selectedCustomer ? (
              <button onClick={clearSelection} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
                <X style={{ width: 14, height: 14 }} />
              </button>
            ) : (
              <ChevronDown style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#94a3b8', pointerEvents: 'none' }} />
            )}
          </div>

          {isOpen && (
            <div style={{
              position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
              background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 12,
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)', zIndex: 200, maxHeight: 220,
              overflowY: 'auto',
            }}>
              {filtered.length === 0 ? (
                <div style={{ padding: '14px 16px', fontSize: 13, color: '#94a3b8', fontFamily: 'Quicksand, sans-serif' }}>
                  No customers found
                </div>
              ) : (
                filtered.map(c => (
                  <div
                    key={c.id}
                    onClick={() => selectCustomer(c)}
                    style={{
                      padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                      transition: 'background 0.12s', display: 'flex', alignItems: 'center', gap: 10,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      width: 32, height: 32, borderRadius: 8, background: '#dbeafe',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: '#2563eb', flexShrink: 0,
                    }}>
                      {(c.name || '?')[0].toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', fontFamily: 'Quicksand, sans-serif' }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.contact || c.phone || c.email || '—'}</div>
                    </div>
                    {selectedCustomer?.id === c.id && (
                      <Check style={{ width: 14, height: 14, color: '#3b82f6', marginLeft: 'auto' }} />
                    )}
                  </div>
                ))
              )}
              <div
                onClick={() => { setIsManual(true); setIsOpen(false); setQuery(''); }}
                style={{
                  padding: '10px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  color: '#3b82f6', background: '#f8fafc', borderTop: '1px solid #e2e8f0',
                  display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Quicksand, sans-serif',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#eff6ff'}
                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}
              >
                <Plus style={{ width: 14, height: 14 }} /> Enter customer manually
              </div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ border: '1.5px solid #bfdbfe', borderRadius: 12, padding: 14, background: '#f0f7ff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#3b82f6' }}>Manual Entry</span>
            <button onClick={clearSelection} style={{ fontSize: 11, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Quicksand, sans-serif', fontWeight: 600 }}>
              ← Back to search
            </button>
          </div>
          <input
            placeholder="Customer name"
            value={customerInfo.name}
            onChange={e => setCustomerInfo(p => ({ ...p, name: e.target.value }))}
            style={inputStyle}
          />
          <input
            placeholder="Phone number"
            value={customerInfo.phone}
            onChange={e => setCustomerInfo(p => ({ ...p, phone: e.target.value }))}
            style={{ ...inputStyle, marginTop: 8 }}
          />
          <input
            placeholder="Email (optional)"
            value={customerInfo.email}
            onChange={e => setCustomerInfo(p => ({ ...p, email: e.target.value }))}
            style={{ ...inputStyle, marginTop: 8 }}
          />
        </div>
      )}

      {selectedCustomer && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <UserCheck style={{ width: 14, height: 14, color: '#16a34a' }} />
          <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}>
            {selectedCustomer.name} · {selectedCustomer.contact || selectedCustomer.phone || selectedCustomer.email}
          </span>
        </div>
      )}
    </div>
  );
};

const inputStyle = {
  width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0',
  borderRadius: 8, fontSize: 13, fontFamily: 'Quicksand, sans-serif',
  fontWeight: 500, outline: 'none', color: '#1e293b', background: '#fff',
  boxSizing: 'border-box',
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ClubPOS = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [discount, setDiscount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({ name: '', phone: '', email: '', table: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [savedOrders, setSavedOrders] = useState([]);
  const [showSavedOrders, setShowSavedOrders] = useState(false);
  const [orderNote, setOrderNote] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [isTaxEnabled, setIsTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState(0.16);
  const [stockAlert, setStockAlert] = useState(null);
  const [showCartModal, setShowCartModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [cashTendered, setCashTendered] = useState(0);
  const [changeDue, setChangeDue] = useState(0);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [shiftStatus, setShiftStatus] = useState({ active: false, startTime: null, cashInDrawer: 0 });
  const [cardDetails, setCardDetails] = useState({ cardNumber: '', expiry: '', cvv: '' });
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [editModal, setEditModal] = useState(false);

  const API_URL = `${config.API_URL}/api`;
  const [formData, setFormData] = useState({
    id: '', cost: '', sellPrice: '', name: '', quantity: '', status: 'active', images: null,
  });

  // ── Menu items ──
  const MenuItem = ({ icon, label, onClick, danger }) => (
    <div onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '9px 14px', cursor: 'pointer', transition: 'background 0.12s',
      color: danger ? '#dc2626' : 'inherit', fontFamily: 'Quicksand, sans-serif',
    }}
      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ width: 15, height: 15, display: 'flex', flexShrink: 0, color: danger ? '#dc2626' : '#64748b' }}>{icon}</span>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
    </div>
  );

  const UserIcon = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="5" r="3"/><path d="M2 14c0-3.3 2.7-6 6-6s6 2.7 6 6"/></svg>;
  const SwitchIcon = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M13 8a5 5 0 0 1-9.9 1H2"/><path d="M2 6a5 5 0 0 1 9.9-1H13"/></svg>;
  const ActivityIcon = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 2"/></svg>;
  const LogoutIcon = () => <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M6 14H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1h3M10 11l3-3-3-3M13 8H6"/></svg>;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchStudents = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${config.API_URL}/api/pullstudents`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setCustomers(res.data.customers || []);
      setUser(res.data.user);
    } catch (err) {
      console.error('Error fetching students:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
      const formPayload = new FormData();
      formPayload.append('id', formData.id);
      formPayload.append('cost', formData.cost);
      formPayload.append('sellPrice', formData.sellPrice);
      formPayload.append('name', formData.name);
      formPayload.append('quantity', formData.quantity);
      formPayload.append('status', formData.status);
      if (formData.images) {
        for (let i = 0; i < formData.images.length; i++) formPayload.append('images[]', formData.images[i]);
      }
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });
      const response = await axios.post(`${config.API_URL}/api/updateproducts`, formPayload, {
        headers: { 'Content-Type': 'multipart/form-data', 'Accept': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        withCredentials: true,
      });
      if (response.data.status == 1) {
        try {
          setIsLoading(true);
          const token = localStorage.getItem('token');
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const params = { page, limit: 20 };
          if (selectedCategory !== 'all') params.category_id = selectedCategory;
          alert('Saved product successfully');
          const response = await axios.get(`${API_URL}/products`, { params, headers });
          setProducts(response.data?.data || []);
          setTotalPages(response.data?.meta?.last_page || 1);
        } catch (error) {
          console.error('Failed to fetch products:', error);
          if (error.response?.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; }
          else { alert('Failed to load products. Please try again.'); setProducts([]); }
        } finally { setIsLoading(false); }
      }
      setEditModal(false);
      setFormData({ cost: '', sellPrice: '', quantity: '', status: 'active', images: null, name: '' });
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Error saving product. Try again.');
    } finally { setIsLoading(false); }
  };

  const generateOrderId = () => 'order_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);

  const formatKES = useCallback((amount) => {
    const numericAmount = Number(amount) || 0;
    const formatted = new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(numericAmount);
    return formatted.replace('KSh', 'Ksh');
  }, []);

  const subtotal = cart.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.quantity) || 0), 0);
  const couponDiscount = appliedCoupon
    ? appliedCoupon.type === 'percentage' ? subtotal * (Number(appliedCoupon.discount) / 100 || 0) : Number(appliedCoupon.discount) || 0
    : 0;
  const manualDiscount = subtotal * (Number(discount) / 100 || 0);
  const totalDiscount = couponDiscount + manualDiscount;
  const taxAmount = isTaxEnabled ? (subtotal - totalDiscount) * (Number(taxRate) || 0) : 0;
  const total = subtotal - totalDiscount + taxAmount;

  const coupons = {
    'CLUB10': { discount: 10, type: 'percentage', description: '10% club member discount' },
    'HAPPY25': { discount: 25, type: 'fixed', description: '$25 off happy hour' },
    'VIP15': { discount: 15, type: 'percentage', description: '15% VIP member discount' },
    'BIRTHDAY20': { discount: 20, type: 'percentage', description: '20% birthday special' },
  };

  const addToCart = useCallback((product) => {
    if (!product || !product.id || !product.price || !product.stock) { alert('Invalid product data'); return; }
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      const newQuantity = existing ? existing.quantity + 1 : 1;
      if (newQuantity > product.stock) {
        setStockAlert(`Cannot add ${product.name || 'item'}. Quantity exceeds stock (${product.stock} available)`);
        return prev;
      }
      if (existing) return prev.map(item => item.id === product.id ? { ...item, quantity: newQuantity } : item);
      return [...prev, { ...product, quantity: 1 }];
    });
  }, []);

  const checkStock = useCallback((product) => {
    if (!product || !product.id) { alert('Invalid product'); return; }
    setFormData(prev => ({ ...prev, cost: product.cost || '', id: product.id || '', sellPrice: product.price || '', name: product.name || '', quantity: product.stock || 0, status: product.status || 'active' }));
    setEditModal(true);
  }, []);

  const updateQuantity = useCallback((id, demand) => {
    setCart(prevCartItems => prevCartItems
      .map(item => {
        if (item.id === id) {
          const newQuantity = item.quantity + demand;
          if (newQuantity > item.stock) { setStockAlert(`Cannot increase ${item.name || 'item'}. Quantity exceeds stock (${item.stock} available)`); return item; }
          return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
        }
        return item;
      }).filter(item => item !== null)
    );
  }, []);

  const removeFromCart = useCallback((id) => setCart(prev => prev.filter(item => item.id !== id)), []);

  const applyCoupon = useCallback(() => {
    const coupon = coupons[couponCode.toUpperCase()];
    if (coupon) { setAppliedCoupon(coupon); setCouponCode(''); }
    else alert('Invalid coupon code');
  }, [couponCode]);

  const removeCoupon = useCallback(() => { setAppliedCoupon(null); setCouponCode(''); }, []);

  const handleBarcodeScan = useCallback((e) => {
    if (e.key === 'Enter' && barcodeInput) {
      const product = products.find(p => p.barcode === barcodeInput);
      if (product) addToCart(product); else alert('Product not found');
      setBarcodeInput('');
    }
  }, [barcodeInput, products, addToCart]);

  const fetchLatestSavedOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_URL}/temp-orders`, { headers });
      setSavedOrders(response.data?.data || []);
    } catch (error) { console.error('Failed to fetch saved orders:', error); setSavedOrders([]); }
  }, []);

  const printReceipt = useCallback((order = null) => {
    const orderData = order || {
      id: generateOrderId(), items: cart, customer: customerInfo,
      order: { subtotal: Number(subtotal) || 0, discount_amount: Number(totalDiscount) || 0, tax_amount: Number(taxAmount) || 0, total_amount: Number(total) || 0, coupon_code: appliedCoupon ? couponCode : null, note: orderNote, payment_method: paymentMethod, cash_tendered: paymentMethod === 'cash' ? Number(cashTendered) || 0 : null, change_due: paymentMethod === 'cash' ? Number(changeDue) || 0 : null },
      appliedCoupon, note: orderNote, timestamp: new Date().toISOString(), status: 'pending'
    };
    const receiptHtml = `
      <div style="font-family: Arial, sans-serif; padding: 20px; width: 300px; border: 1px solid #ccc;">
        <h2 style="text-align: center; color: #4B0082;">{config.STORE_NAME}</h2>
        <p style="text-align: center; font-size: 12px; color: #666;">{config.STORE_SLOGAN}</p>
        <p style="text-align: center; font-size: 12px; color: #666;">Receipt #${orderData.id}</p>
        <p style="text-align: center; font-size: 12px; color: #666;">${new Date(orderData.timestamp).toLocaleString()}</p>
        <hr style="border: 1px dashed #ccc; margin: 10px 0;" />
        <h3 style="font-size: 14px; color: #333;">Order Details</h3>
        ${orderData.customer.name ? `<p style="font-size: 12px;">Customer: ${orderData.customer.name}</p>` : ''}
        ${orderData.customer.table ? `<p style="font-size: 12px;">Order: ${orderData.customer.table}</p>` : ''}
        ${orderData.note ? `<p style="font-size: 12px;">Note: ${orderData.note}</p>` : ''}
        <table style="width: 100%; font-size: 12px; margin-top: 10px;">
          <thead><tr style="border-bottom: 1px solid #ccc;"><th style="text-align: left;">Item</th><th style="text-align: center;">Qty</th><th style="text-align: right;">Price</th></tr></thead>
          <tbody>${orderData.items.map(item => `<tr><td>${item.name || `Product ${item.product_id || 'Unknown'}`}</td><td style="text-align: center;">${Number(item.quantity) || 0}</td><td style="text-align: right;">${formatKES(Number(item.subtotal) || 0)}</td></tr>`).join('')}</tbody>
        </table>
        <hr style="border: 1px dashed #ccc; margin: 10px 0;" />
        <p style="font-size: 12px;">Subtotal: ${formatKES(orderData.order.subtotal)}</p>
        ${orderData.order.discount_amount > 0 ? `<p style="font-size: 12px; color: green;">Discount: -${formatKES(orderData.order.discount_amount)}</p>` : ''}
        ${orderData.order.tax_amount > 0 ? `<p style="font-size: 12px;">Tax (${(Number(taxRate) * 100).toFixed(0)}%): ${formatKES(orderData.order.tax_amount)}</p>` : ''}
        <p style="font-size: 14px; font-weight: bold;">Total: ${formatKES(orderData.order.total_amount)}</p>
        ${orderData.order.payment_method ? `<p style="font-size: 12px;">Payment Method: ${orderData.order.payment_method.charAt(0).toUpperCase() + orderData.order.payment_method.slice(1)}</p>` : ''}
        ${orderData.order.cash_tendered ? `<p style="font-size: 12px;">Cash Tendered: ${formatKES(orderData.order.cash_tendered)}</p>` : ''}
        ${orderData.order.change_due ? `<p style="font-size: 12px;">Change Due: ${formatKES(orderData.order.change_due)}</p>` : ''}
      </div>`;
    const element = document.createElement('div');
    element.innerHTML = receiptHtml;
    document.body.appendChild(element);
    html2pdf().set({ filename: `receipt_${orderData.id}.pdf`, margin: [10,10,10,10], jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' } }).from(element).save().then(() => { document.body.removeChild(element); alert('Receipt downloaded as PDF!'); }).catch(error => { console.error('Failed to generate PDF:', error); document.body.removeChild(element); alert('Failed to generate PDF. Please try again.'); });
  }, [cart, customerInfo, appliedCoupon, couponCode, orderNote, paymentMethod, cashTendered, changeDue, subtotal, totalDiscount, taxAmount, total, taxRate, formatKES]);

  const deleteSavedOrder = useCallback(async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.get(`${API_URL}/delete-orders/${orderId}`, { headers });
      setSavedOrders(prev => prev.filter(order => order.id !== orderId));
    } catch (error) { console.error('Failed to delete saved order:', error); alert('Failed to delete saved order. Please try again.'); }
  }, []);

  const prepareCartData = useCallback(() => ({
    items: cart.map(item => ({ product_id: item.id, quantity: Number(item.quantity) || 0, unit_price: Number(item.price) || 0, subtotal: (Number(item.price) || 0) * (Number(item.quantity) || 0) })),
    customer: { name: customerInfo.name || null, phone: customerInfo.phone || null, email: customerInfo.email || null, table_number: customerInfo.table || null },
    order: { subtotal: Number(subtotal) || 0, discount_amount: Number(totalDiscount) || 0, tax_amount: Number(taxAmount) || 0, total_amount: Number(total) || 0, coupon_code: appliedCoupon ? couponCode : null, note: orderNote || null, payment_method: paymentMethod },
    appliedCoupon, note: orderNote, timestamp: new Date().toISOString(), status: 'pending'
  }), [cart, customerInfo, appliedCoupon, couponCode, orderNote, paymentMethod, subtotal, totalDiscount, taxAmount, total]);

  const preparePaymentData = useCallback(() => ({
    order: { total_amount: Number(total) || 0, payment_method: paymentMethod, customer_phone: paymentMethod === 'mpesa' ? mpesaPhone : customerInfo.phone, cash_tendered: paymentMethod === 'cash' ? Number(cashTendered) || 0 : null, change_due: paymentMethod === 'cash' ? Number(changeDue) || 0 : null, card_details: paymentMethod === 'card' ? cardDetails : null, status: 'completed' }
  }), [total, paymentMethod, mpesaPhone, customerInfo.phone, cashTendered, changeDue, cardDetails]);

  const saveOrder = useCallback(async () => {
    if (cart.length === 0) { alert('Cart is empty'); return; }
    const cartData = prepareCartData();
    const token = localStorage.getItem('token');
    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
      const response = await axios.post(`${config.API_URL}/api/save-orders`, cartData, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}`, 'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || '') }, withCredentials: true });
      setSavedOrders(prev => [...prev, response.data?.data || cartData]);
      setCart([]); setAppliedCoupon(null); setDiscount(0); setCustomerInfo({ name: '', phone: '', email: '', table: '' }); setOrderNote(''); setShowCartModal(false);
      fetchLatestSavedOrders();
      alert('Order saved successfully! Receipt can be printed.');
    } catch (error) { console.error('Failed to save order:', error.response?.data || error.message); alert('Failed to save order: ' + (error.response?.data?.message || 'Please try again.')); }
  }, [cart, prepareCartData, fetchLatestSavedOrders]);

  const loadSavedOrder = useCallback((order) => {
    if (!order?.items || !Array.isArray(order.items)) { alert('Invalid order data'); return; }
    const mappedItems = order.items.map(savedItem => {
      const product = products.find(p => p.id === savedItem.product_id || String(p.id) === String(savedItem.product_id));
      if (!product) return { id: savedItem.product_id, name: `Unknown Item (${savedItem.product_id})`, price: savedItem.unit_price, quantity: savedItem.quantity, stock: 0, subtotal: savedItem.subtotal };
      return { id: savedItem.product_id, name: product.name || `Unknown Item (${savedItem.product_id})`, price: savedItem.unit_price, quantity: savedItem.quantity, stock: product.stock || 0, subtotal: savedItem.subtotal };
    });
    if (mappedItems.length === 0) { alert('No valid items found in saved order'); return; }
    setCart(mappedItems); setCustomerInfo(order.customer || { name: '', phone: '', email: '', table: '' }); setAppliedCoupon(order.appliedCoupon || null); setOrderNote(order.note || ''); setShowSavedOrders(false); setShowCartModal(true);
  }, [products]);

  const startShift = useCallback(async (initialCash) => {
    const cash = Number(initialCash) || 0;
    if (isNaN(cash) || cash < 0) { alert('Invalid initial cash amount'); return; }
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/cash-register/start`, { initial_cash: cash }, { headers });
      setShiftStatus({ active: true, startTime: new Date().toISOString(), cashInDrawer: cash });
      alert('Shift started');
    } catch (error) { console.error('Failed to start shift:', error); alert('Failed to start shift. Please try again.'); }
  }, []);

  const endShift = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/cash-register/end`, {}, { headers });
      setShiftStatus({ active: false, startTime: null, cashInDrawer: 0 });
      alert('Shift ended');
    } catch (error) { console.error('Failed to end shift:', error); alert('Failed to end shift. Please try again.'); }
  }, []);

  const voidTransaction = useCallback(() => {
    if (cart.length === 0) { alert('No transaction to clear'); return; }
    setCart([]); setAppliedCoupon(null); setDiscount(0); setCustomerInfo({ name: '', phone: '', email: '', table: '' }); setOrderNote(''); setCashTendered(0); setChangeDue(0); setShowCartModal(false);
    alert('Transaction cleared');
  }, [cart]);

  const handleCashTendered = useCallback((amount) => {
    const tendered = Number(amount) || 0;
    setCashTendered(tendered);
    setChangeDue(tendered >= total ? (tendered - total) : 0);
  }, [total]);

  const processPayment = useCallback(async () => {
    if (cart.length === 0) { alert('Cart is empty'); return; }
    if (paymentMethod === 'cash' && (Number(cashTendered) < total || isNaN(Number(cashTendered)))) { alert('Invalid or insufficient cash tendered'); return; }
    if (paymentMethod === 'mpesa' && !mpesaPhone) { alert('Please enter an M-Pesa phone number'); return; }
    if (paymentMethod === 'card' && (!cardDetails.cardNumber || !cardDetails.expiry || !cardDetails.cvv)) { alert('Please complete all card details'); return; }
    setIsProcessing(true);
    try {
      const token = localStorage.getItem('token');
      const cartData = prepareCartData();
      const paymentData = preparePaymentData();
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
      const orderResponse = await axios.post(`${config.API_URL}/api/postorders`, cartData, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}`, 'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || '') }, withCredentials: true });
      const paymentResponse = await axios.post(`${config.API_URL}/api/postpayment`, { ...paymentData, order_id: orderResponse.data.order_id }, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}`, 'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || '') }, withCredentials: true });
      if (paymentMethod === 'cash') {
        try {
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          await axios.post(`${API_URL}/cash-register/update`, { cash_tendered: Number(cashTendered) || 0 }, { headers });
        } catch (error) { console.error('Failed to update cash register:', error); }
        setShiftStatus(prev => ({ ...prev, cashInDrawer: prev.cashInDrawer + (Number(cashTendered) || 0) }));
      }
      const newTransaction = { id: orderResponse.data.order_id || generateOrderId(), ...cartData, payment_method: paymentMethod, cash_tendered: paymentMethod === 'cash' ? Number(cashTendered) || 0 : null, change_due: paymentMethod === 'cash' ? Number(changeDue) || 0 : null, card_details: paymentMethod === 'card' ? cardDetails : null, mpesa_phone: paymentMethod === 'mpesa' ? mpesaPhone : null, timestamp: new Date().toISOString(), status: 'completed' };
      setTransactions(prev => [...prev, newTransaction]);
      if (cartData.id) await deleteSavedOrder(cartData.id);
      setIsProcessing(false); setShowPayment(false); setCart([]); setAppliedCoupon(null); setDiscount(0); setCustomerInfo({ name: '', phone: '', email: '', table: '' }); setOrderNote(''); setCashTendered(0); setChangeDue(0); setShowCartModal(false); setCardDetails({ cardNumber: '', expiry: '', cvv: '' }); setMpesaPhone('');
      alert('Payment processed successfully!');
      printReceipt(newTransaction);
    } catch (error) { console.error('Payment processing failed:', error); setIsProcessing(false); alert('Payment processing failed. Please try again.'); }
  }, [cart, paymentMethod, cashTendered, total, mpesaPhone, cardDetails, prepareCartData, preparePaymentData, printReceipt, deleteSavedOrder]);

  const processRefund = useCallback(async (order) => {
    if (!order?.id || !order.order?.total_amount) { alert('Invalid order data for refund'); return; }
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/refunds`, { order_id: order.id, total_amount: Number(order.order.total_amount) || 0, payment_method: order.order.payment_method }, { headers });
      if (order.order.payment_method === 'cash') {
        try { await axios.post(`${API_URL}/cash-register/update`, { cash_refunded: Number(order.order.total_amount) || 0 }, { headers }); }
        catch (error) { console.error('Failed to update cash register for refund:', error); }
        setShiftStatus(prev => ({ ...prev, cashInDrawer: prev.cashInDrawer - (Number(order.order.total_amount) || 0) }));
      }
      setTransactions(prev => prev.map(t => t.id === order.id ? { ...t, status: 'refunded' } : t));
      await deleteSavedOrder(order.id);
      alert('Refund processed successfully');
    } catch (error) { console.error('Refund processing failed:', error); alert('Refund processing failed. Please try again.'); }
  }, [deleteSavedOrder]);

  useEffect(() => {
    fetchStudents();
    const storedTransactions = localStorage.getItem('clubpos_transactions');
    setTransactions(storedTransactions ? JSON.parse(storedTransactions) : []);
  }, []);

  useEffect(() => {
    const fetchSavedOrders = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API_URL}/temp-orders`, { headers });
        setSavedOrders(response.data?.data || []);
      } catch (error) { console.error('Failed to fetch saved orders:', error); setSavedOrders([]); }
    };
    fetchSavedOrders();
  }, []);

  useEffect(() => { localStorage.setItem('clubpos_transactions', JSON.stringify(transactions)); }, [transactions]);
  useEffect(() => { const timer = setInterval(() => setCurrentTime(new Date()), 60000); return () => clearInterval(timer); }, []);
  useEffect(() => { const handleResize = () => setIsMobile(window.innerWidth < 768); window.addEventListener('resize', handleResize); return () => window.removeEventListener('resize', handleResize); }, []);
  useEffect(() => { if (stockAlert) { alertSound.play().catch(error => console.error('Audio play failed:', error)); setTimeout(() => setStockAlert(null), 3000); } }, [stockAlert]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const categoriesRes = await axios.get(`${API_URL}/categories`, { headers });
        setCategories(categoriesRes.data?.adminCategories || []);
      } catch (error) { console.error('Failed to fetch categories:', error); setCategories([]); }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const params = { page, limit: 20 };
        if (selectedCategory !== 'all') params.category_id = selectedCategory;
        const response = await axios.get(`${API_URL}/products`, { params, headers });
        setProducts(response.data?.data || []);
        setTotalPages(response.data?.meta?.last_page || 1);
      } catch (error) {
        console.error('Failed to fetch products:', error);
        if (error.response?.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; }
        else { alert('Failed to load products. Please try again.'); setProducts([]); }
      } finally { setIsLoading(false); }
    };
    fetchProducts();
  }, [page, selectedCategory]);

  const onSignOut = () => { localStorage.removeItem("token"); navigate("/"); };
  const goToHome = () => { const token = localStorage.getItem('token'); window.location.assign(config.API_URL + '/authenticate-user/' + token, '_blank'); };

  const filteredProducts = products.filter(product => {
    const name = String(product.name ?? '').toLowerCase();
    const description = String(product.description ?? '').toLowerCase();
    const search = searchTerm.trim().toLowerCase();
    //const selectedCat = String(selectedCategory ?? 'all');
    const productCat = String(product.category ?? '');
    const matchesSearch = name.includes(search) || productCat.toLowerCase().includes(search) || description.includes(search);
   // const matchesCategory = selectedCat === 'all' || productCat === selectedCat;
   // return matchesCategory && matchesSearch;
    return matchesSearch;
  });

  const cartContentProps = {
    cart, customerInfo, setCustomerInfo, orderNote, setOrderNote, couponCode, setCouponCode,
    appliedCoupon, applyCoupon, removeCoupon, discount, setDiscount, isTaxEnabled, setIsTaxEnabled,
    taxRate, setTaxRate, subtotal, couponDiscount, manualDiscount, taxAmount, total, formatKES,
    updateQuantity, removeFromCart, saveOrder, printReceipt, setShowPayment, voidTransaction,
    setShowTransactionHistory, setShowCartModal, isMobile, shiftStatus, endShift, startShift,
    customers, // pass customers so CartContent can also use CustomerSelector if needed
  };

  // ── Shared modal/panel styles ──
  const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 };
  const modalCard = { background: '#fff', borderRadius: 20, padding: 28, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.18)', fontFamily: 'Quicksand, sans-serif' };
  const modalHeader = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 };
  const modalTitle = { fontSize: 20, fontWeight: 700, color: '#0f172a' };
  const closeBtn = { background: '#f1f5f9', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'Quicksand, sans-serif' }}>

      {/* Stock Alert */}
      {stockAlert && (
        <div style={{ position: 'fixed', top: 20, right: 20, background: '#fef2f2', border: '1.5px solid #fecaca', color: '#dc2626', padding: '12px 18px', borderRadius: 14, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 8px 24px rgba(220,38,38,0.15)', fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: 14 }}>
          <Bell style={{ width: 18, height: 18 }} />
          {stockAlert}
        </div>
      )}

      <BottomBar/>

      <div style={{ display: 'flex', height: '100vh' }}>

        {/* ── Left Panel: Products ── */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>

          {/* Topbar */}
          <div style={{ background: '#ffffff', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', position: 'sticky', top: 0, zIndex: 40 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width:"100px"}}>
                <img src="https://finance.insigniah2o.co.ke/cms/public/InsigniahLogo.png" style={{ width: "90%", height: "54px",marginRight:"10px" }} />
              </div>
              {/*<div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: '5px 10px' }}>
                <Clock style={{ width: 13, height: 13, color: 'rgba(255,255,255,0.7)' }} />
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>*/}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Saved Orders Button */}
              <button
                onClick={() => setShowSavedOrders(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.25)', borderRadius: 10, color: '#222', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'Quicksand, sans-serif', transition: 'background 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
              >
                <Clock style={{ width: 15, height: 15 }} />
                Orders
                {savedOrders.length > 0 && (
                  <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 20, padding: '2px 6px' }}>
                    {savedOrders.length}
                  </span>
                )}
              </button>

              {/* User Menu */}
              <span ref={wrapRef} style={{ position: 'relative', display: 'inline-block' }}>
                <button onClick={() => setOpen(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontFamily: 'Quicksand, sans-serif' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 8, background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>
                    {(user || 'U')[0]}
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{user || 'Menu'}</span>
                  <ChevronDown style={{ width: 13, height: 13, color: '#64748b', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                </button>

                {open && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 220, background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', zIndex: 1000, boxShadow: '0 12px 32px rgba(0,0,0,0.12)' }}>
                    <div style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{user}</div>
                    </div>
                     <div style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <MenuItem icon={<SwitchIcon />} label="Manage Shift" onClick={() => navigate('/ManageShift')} />
                    </div>
                    <div style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <MenuItem icon={<SwitchIcon />} label="Expenses" onClick={() => navigate('/manage-expenses')} />
                    </div>
                    <div style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <MenuItem icon={<SwitchIcon />} label="Expenses Voteheads" onClick={() => navigate('/manageVoteheads/Expense')} />
                    </div>
                    <div style={{ padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
                      <MenuItem icon={<ActivityIcon />} label="Go To Admin System" onClick={goToHome} />
                    </div>
                    <div style={{ padding: '6px 0' }}>
                      <MenuItem icon={<LogoutIcon />} label="Log out" onClick={onSignOut} danger />
                    </div>
                  </div>
                )}
              </span>
            </div>
          </div>

          {/* Search + Categories */}
          <div style={{ padding: '16px 20px 0', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ position: 'relative', marginBottom: 14 }}>
              <Search style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Search products by name, category…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ width: '100%', padding: '11px 14px 11px 42px', border: '1.5px solid #e2e8f0', borderRadius: 12, fontSize: 13, fontFamily: 'Quicksand, sans-serif', fontWeight: 500, color: '#1e293b', background: '#f8fafc', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
                onFocus={e => e.target.style.borderColor = '#93c5fd'}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 14 }}>
              {categories?.length<1 && (
              <button
                onClick={() => setSelectedCategory('all')}
                style={{ padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', background: selectedCategory === 'all' ? '#004080' : '#f1f5f9', color: selectedCategory === 'all' ? '#fff' : '#64748b', transition: 'all 0.2s' }}
              >
                All Items
              </button>
              )}
              {categories.map(category => (
                <button
                  key={category.id || category.name}
                  onClick={() => setSelectedCategory(category.id || 'all')}
                  style={{ padding: '7px 16px', borderRadius: 20, border: 'none', cursor: 'pointer', fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', background: selectedCategory === category.id ? '#004080' : '#f1f5f9', color: selectedCategory === category.id ? '#fff' : '#64748b', transition: 'all 0.2s' }}
                >
                  {category.name || 'Unknown'}
                </button>
              ))}
            </div>
          </div>

          {/* Products Grid */}
          <div style={{ padding: 20, flex: 1 }}>
            {isLoading ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14, fontWeight: 600 }}>
                <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#004080', borderRadius: '50%', margin: '0 auto 14px', animation: 'spin 0.8s linear infinite' }} />
                Loading products…
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 16 }}>
                  {filteredProducts.length === 0 ? (
                    <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: 14, fontWeight: 600 }}>No products found</div>
                  ) : (
                    filteredProducts.map(product => {
                      const outOfStock = Number(product.stock) < 1;
                      return (
                        <div
                          key={product.id || product.name}
                          style={{ background: '#fff', borderRadius: 16, padding: 16, border: '1.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: 10, transition: 'all 0.2s', cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,64,128,0.10)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.04)'; }}
                        >
                          <div style={{ position: 'relative', background: '#f8fafc', borderRadius: 10, overflow: 'hidden', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                              src={product.image_url || product.image || 'https://via.placeholder.com/100'}
                              alt={product.name || 'Product'}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={e => { e.target.src = 'https://via.placeholder.com/100'; }}
                            />
                            {outOfStock && (
                              <div style={{ position: 'absolute', top: 6, right: 6, background: '#ef4444', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 7px', borderRadius: 20 }}>OUT</div>
                            )}
                          </div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 4, lineHeight: 1.3 }}>{product.name || 'Unknown Product'}</p>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: 14, fontWeight: 700, color: '#004080' }}>{formatKES(Number(product.price) || 0)}</span>
                              <span
                                onClick={() => checkStock(product)}
                                style={{ fontSize: 10, color: outOfStock ? '#dc2626' : '#64748b', background: outOfStock ? '#fef2f2' : '#f1f5f9', padding: '2px 8px', borderRadius: 20, fontWeight: 600, cursor: 'pointer' }}
                              >
                                Qty: {Number(product.stock) || 0}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => { if (outOfStock) checkStock(product); else addToCart(product); }}
                            style={{
                              padding: '9px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                              fontSize: 12, fontWeight: 700, fontFamily: 'Quicksand, sans-serif',
                              color: '#fff', letterSpacing: '0.02em', transition: 'all 0.2s',
                              background: outOfStock ? 'linear-gradient(135deg, #dc2626, #f59e0b)' : 'linear-gradient(135deg, #004080, #0066cc)',
                            }}
                          >
                            {outOfStock ? ' Restock' : '+ Add to Cart'}
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Pagination */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 24, paddingBottom: 20 }}>
                  <button
                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    style={{ padding: '8px 20px', borderRadius: 10, border: 'none', background: page === 1 ? '#e2e8f0' : '#004080', color: page === 1 ? '#94a3b8' : '#fff', fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: 13, cursor: page === 1 ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                  >
                    ← Prev
                  </button>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#64748b' }}>Page {page} of {totalPages}</span>
                  <button
                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={page === totalPages}
                    style={{ padding: '8px 20px', borderRadius: 10, border: 'none', background: page === totalPages ? '#e2e8f0' : '#004080', color: page === totalPages ? '#94a3b8' : '#fff', fontFamily: 'Quicksand, sans-serif', fontWeight: 600, fontSize: 13, cursor: page === totalPages ? 'not-allowed' : 'pointer', transition: 'all 0.2s' }}
                  >
                    Next →
                  </button>
                </div>
                <br/><br/><br/><br/><br/><br/>
              </>
            )}
          </div>
        </div>

        {/* ── Right Panel: Cart (desktop) ── */}
        {!isMobile && (
          <div style={{ width: 380, background: '#fff', borderLeft: '1.5px solid #e2e8f0', display: 'flex', flexDirection: 'column', fontFamily: 'Quicksand, sans-serif' }}>
            {/* Customer Selector in cart panel */}
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <CustomerSelector customers={customers} customerInfo={customerInfo} setCustomerInfo={setCustomerInfo} />
              <input
                placeholder="Table / Order label (optional)"
                value={customerInfo.table}
                onChange={e => setCustomerInfo(p => ({ ...p, table: e.target.value }))}
                style={{ ...inputStyle, marginTop: 10 }}
              />
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <CartContent {...cartContentProps} />
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile Cart FAB ── */}
      {isMobile && (
        <button
          onClick={() => setShowCartModal(true)}
          style={{ position: 'fixed', bottom: 74, right: 24, background: '#004080', color: '#fff', width: 58, height: 58, borderRadius: '50%', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(0,64,128,0.35)', zIndex: 50 }}
        >
          <ShoppingCart style={{ width: 24, height: 24 }} />
          {cart.length > 0 && (
            <span style={{ position: 'absolute', top: -4, right: -4, background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)}
            </span>
          )}
        </button>
      )}

      {/* ── Mobile Cart Modal ── */}
      {isMobile && showCartModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: '#fff', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', fontFamily: 'Quicksand, sans-serif' }}>
            <div style={{ padding: '16px 18px', borderBottom: '1px solid #f1f5f9' }}>
              <CustomerSelector customers={customers} customerInfo={customerInfo} setCustomerInfo={setCustomerInfo} />
              <input
                placeholder="Table / Order label (optional)"
                value={customerInfo.table}
                onChange={e => setCustomerInfo(p => ({ ...p, table: e.target.value }))}
                style={{ ...inputStyle, marginTop: 10 }}
              />
            </div>
            <CartContent {...cartContentProps} />
          </div>
        </div>
      )}

      {/* ── Saved Orders Modal ── */}
      {showSavedOrders && (
        <div style={modalOverlay}>
          <div style={{ ...modalCard, maxWidth: 600 }}>
            <div style={modalHeader}>
              <span style={modalTitle}>Saved Orders</span>
              <button onClick={() => setShowSavedOrders(false)} style={closeBtn}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {savedOrders.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14, fontWeight: 600 }}>No saved orders yet</p>
              ) : (
                savedOrders.map(order => (
                  <div key={order.id} style={{ background: '#f8fafc', borderRadius: 14, padding: 16, border: '1.5px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                      <div>
                        <p style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{order.customer?.name || 'Order'} · {order.customer?.table || 'N/A'}</p>
                        <p style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(order.timestamp).toLocaleString()}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontWeight: 700, color: '#004080', fontSize: 16 }}>{formatKES(Number(order.order?.total_amount || 0).toFixed(2))}</p>
                        <p style={{ fontSize: 12, color: '#94a3b8' }}>{order.items?.length || 0} items</p>
                      </div>
                    </div>
                    {order.note && <p style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>Note: {order.note}</p>}
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[{ label: 'Load Order', bg: '#004080', action: () => loadSavedOrder(order) }, { label: 'Print Receipt', bg: '#16a34a', action: () => printReceipt(order) }, { label: 'Delete', bg: '#dc2626', action: () => deleteSavedOrder(order.id) }].map(btn => (
                        <button key={btn.label} onClick={btn.action} style={{ flex: 1, padding: '8px 0', borderRadius: 10, border: 'none', background: btn.bg, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Quicksand, sans-serif', transition: 'opacity 0.2s' }}
                          onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                        >{btn.label}</button>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Restock Modal ── */}
      {editModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <span style={modalTitle}>Restock Product</span>
              <button onClick={() => setEditModal(false)} style={closeBtn}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {[
                { label: 'Product Name', name: 'name', type: 'text', placeholder: 'Enter product name' },
                { label: 'Cost', name: 'cost', type: 'number', placeholder: 'Enter cost' },
                { label: 'Sell Price', name: 'sellPrice', type: 'number', placeholder: 'Enter selling price' },
                { label: 'Quantity', name: 'quantity', type: 'number', placeholder: 'Enter quantity' },
              ].map(field => (
                <div key={field.name}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>{field.label}</label>
                  <input type={field.type} name={field.name} value={formData[field.name]} onChange={handleChange} placeholder={field.placeholder} style={{ ...inputStyle, border: '1.5px solid #e2e8f0' }} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Product Images</label>
                <input type="file" name="images" multiple accept="image/*" onChange={e => setFormData(prev => ({ ...prev, images: e.target.files }))} style={{ ...inputStyle, border: '1.5px solid #e2e8f0', padding: '8px 12px' }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', marginBottom: 6 }}>Status</label>
                <select name="status" value={formData.status} onChange={handleChange} style={{ ...inputStyle, border: '1.5px solid #e2e8f0' }}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <button
                type="button"
                onClick={handleSubmit}
                style={{ padding: '11px 0', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #004080, #0066cc)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Quicksand, sans-serif', transition: 'opacity 0.2s', marginTop: 4 }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Save Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Transaction History Modal ── */}
      {showTransactionHistory && (
        <div style={modalOverlay}>
          <div style={{ ...modalCard, maxWidth: 600 }}>
            <div style={modalHeader}>
              <span style={modalTitle}>Transaction History</span>
              <button onClick={() => setShowTransactionHistory(false)} style={closeBtn}><X style={{ width: 16, height: 16 }} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {transactions.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#94a3b8', padding: '40px 0', fontSize: 14, fontWeight: 600 }}>No transactions yet</p>
              ) : (
                transactions.map(transaction => (
                  <div key={transaction.id} style={{ background: '#f8fafc', borderRadius: 14, padding: 16, border: '1.5px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div>
                        <p style={{ fontWeight: 700, color: '#1e293b', fontSize: 13 }}>{transaction.customer?.name || 'Order'} · {transaction.payment_method || 'Unknown'}</p>
                        <p style={{ fontSize: 12, color: '#94a3b8' }}>{new Date(transaction.timestamp).toLocaleString()}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontWeight: 700, color: '#004080', fontSize: 15 }}>{formatKES(Number(transaction.order?.total_amount || 0).toFixed(2))}</p>
                        {transaction.status === 'refunded' && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 700 }}>REFUNDED</span>}
                      </div>
                    </div>
                    {transaction.payment_method === 'cash' && (
                      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                        <span>Cash: {formatKES(Number(transaction.cash_tendered) || 0)}</span> · <span>Change: {formatKES(Number(transaction.change_due) || 0)}</span>
                      </div>
                    )}
                    <button onClick={() => printReceipt(transaction)} style={{ width: '100%', padding: '8px 0', borderRadius: 10, border: 'none', background: '#16a34a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Quicksand, sans-serif' }}>
                      Print Receipt
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Payment Modal ── */}
      {showPayment && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <div style={modalHeader}>
              <span style={modalTitle}>Process Payment</span>
              <button onClick={() => setShowPayment(false)} style={closeBtn}><X style={{ width: 16, height: 16 }} /></button>
            </div>

            {/* Total summary */}
            <div style={{ background: 'linear-gradient(135deg, #004080, #0066cc)', borderRadius: 14, padding: '18px 20px', marginBottom: 20 }}>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontWeight: 600, marginBottom: 4 }}>Total Amount</p>
              <p style={{ fontSize: 32, fontWeight: 700, color: '#fff' }}>{formatKES(total.toFixed(2))}</p>
              {customerInfo.name && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>Customer: {customerInfo.name}{customerInfo.table ? ` · Table ${customerInfo.table}` : ''}</p>}
            </div>

            {/* Payment method selector */}
            <div style={{ marginBottom: 18 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: '#64748b', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>Payment Method</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                {[
                  { method: 'card', icon: <CreditCard style={{ width: 18, height: 18 }} />, label: 'Card' },
                  { method: 'mpesa', icon: <Smartphone style={{ width: 18, height: 18 }} />, label: 'M-Pesa' },
                  { method: 'cash', icon: <Wallet style={{ width: 18, height: 18 }} />, label: 'Cash' },
                   { method: 'credit', icon: <CreditCard style={{ width: 18, height: 18 }} />, label: 'Credit' },
                ].map(pm => (
                  <button
                    key={pm.method}
                    onClick={() => setPaymentMethod(pm.method)}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '12px 8px', borderRadius: 12, border: `2px solid ${paymentMethod === pm.method ? '#004080' : '#e2e8f0'}`, background: paymentMethod === pm.method ? '#eff6ff' : '#f8fafc', color: paymentMethod === pm.method ? '#004080' : '#64748b', cursor: 'pointer', fontFamily: 'Quicksand, sans-serif', fontWeight: 700, fontSize: 12, transition: 'all 0.2s' }}
                  >
                    {pm.icon}{pm.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment details */}
            {paymentMethod === 'card' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 18 }}>
                <input type="text" placeholder="Card Number" value={cardDetails.cardNumber} onChange={e => setCardDetails(p => ({ ...p, cardNumber: e.target.value }))} style={{ ...inputStyle, border: '1.5px solid #e2e8f0' }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input type="text" placeholder="MM/YY" value={cardDetails.expiry} onChange={e => setCardDetails(p => ({ ...p, expiry: e.target.value }))} style={{ ...inputStyle, border: '1.5px solid #e2e8f0' }} />
                  <input type="text" placeholder="CVV" value={cardDetails.cvv} onChange={e => setCardDetails(p => ({ ...p, cvv: e.target.value }))} style={{ ...inputStyle, border: '1.5px solid #e2e8f0' }} />
                </div>
              </div>
            ) : paymentMethod === 'mpesa' ? (
              <div style={{ marginBottom: 18 }}>
                <input type="tel" placeholder="M-Pesa Phone Number" value={mpesaPhone} onChange={e => setMpesaPhone(e.target.value)} style={{ ...inputStyle, border: '1.5px solid #e2e8f0', marginBottom: 10 }} />
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                  You will receive an M-Pesa prompt to complete payment.
                </div>
              </div>
            ) : paymentMethod === 'credit' ? (
              <div style={{ marginBottom: 18 }}>
        
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                 This will be saved as an unpaid transaction
                </div>
              </div>
            ): (
              <div style={{ marginBottom: 18 }}>
                <input type="number" placeholder="Cash Tendered (Ksh)" value={cashTendered || ''} onChange={e => handleCashTendered(e.target.value)} style={{ ...inputStyle, border: '1.5px solid #e2e8f0', marginBottom: 10 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
                  {[500, 1000, 5000].map(amount => (
                    <button key={amount} onClick={() => handleCashTendered(amount)} style={{ padding: '9px 0', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Quicksand, sans-serif' }}>
                      Ksh {amount.toLocaleString()}
                    </button>
                  ))}
                </div>
                {cashTendered > 0 && (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', fontSize: 13, color: '#16a34a', fontWeight: 700 }}>
                    Change Due: {formatKES(Number(changeDue) || 0)}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={processPayment}
              disabled={isProcessing || (paymentMethod === 'cash' && (Number(cashTendered) < total || isNaN(Number(cashTendered))))}
              style={{ width: '100%', padding: '13px 0', borderRadius: 12, border: 'none', background: isProcessing || (paymentMethod === 'cash' && Number(cashTendered) < total) ? '#cbd5e1' : 'linear-gradient(135deg, #004080, #0066cc)', color: '#fff', fontSize: 15, fontWeight: 700, cursor: isProcessing ? 'wait' : 'pointer', fontFamily: 'Quicksand, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, transition: 'opacity 0.2s' }}
            >
              {isProcessing ? (
                <><div style={{ width: 18, height: 18, border: '2.5px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />Processing…</>
              ) : (
                <><Check style={{ width: 18, height: 18 }} />Complete Payment</>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClubPOS;